import { motion } from 'framer-motion'
import { useInView } from 'framer-motion'
import { useRef, useState, FormEvent } from 'react'
import { Link } from 'react-router-dom'
import type { Contact as ContactType, Footer as FooterType } from '../types/content'
import { Phone, Mail, MapPin } from './Icons'

interface ContactProps {
  data: ContactType
  footer: FooterType
}

export default function Contact({ data, footer }: ContactProps) {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)

    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000))

    alert('Спасибо за заявку! Мы свяжемся с вами в ближайшее время.')
    setIsSubmitting(false)
    ;(e.target as HTMLFormElement).reset()
  }

  return (
    <section id="contact" className="section" ref={ref}>
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="section-header"
        >
          <h2 className="section-title">{data.title}</h2>
          <p className="section-subtitle">{data.subtitle}</p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="space-y-6"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg text-primary">
                <Phone />
              </div>
              <div>
                <div className="text-sm text-gray-400">Телефон</div>
                <a href={`tel:${footer.contacts.phone.replace(/\s/g, '')}`} className="hover:text-primary transition-colors">
                  {footer.contacts.phone}
                </a>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg text-primary">
                <Mail />
              </div>
              <div>
                <div className="text-sm text-gray-400">Email</div>
                <a href={`mailto:${footer.contacts.email}`} className="hover:text-primary transition-colors">
                  {footer.contacts.email}
                </a>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg text-primary">
                <MapPin />
              </div>
              <div>
                <div className="text-sm text-gray-400">Адрес</div>
                <span>{footer.company.address}</span>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.form
            initial={{ opacity: 0, x: 20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.3 }}
            onSubmit={handleSubmit}
            className="bg-dark-secondary border border-border rounded-xl p-8"
          >
            {data.form.fields.map(field => (
              <div key={field.name} className="mb-4">
                <label htmlFor={field.name} className="block text-sm text-gray-400 mb-2">
                  {field.label}{field.required && ' *'}
                </label>
                {field.type === 'textarea' ? (
                  <textarea
                    id={field.name}
                    name={field.name}
                    placeholder={field.placeholder}
                    required={field.required}
                    className="w-full px-4 py-3 bg-dark-tertiary border border-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary transition-colors min-h-[150px] resize-y"
                  />
                ) : (
                  <input
                    type={field.type}
                    id={field.name}
                    name={field.name}
                    placeholder={field.placeholder}
                    required={field.required}
                    className="w-full px-4 py-3 bg-dark-tertiary border border-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary transition-colors"
                  />
                )}
              </div>
            ))}

            {/* Ссылка вынесена из <label>: иначе клик по ней заодно
                переключал бы чекбокс. Доступное имя чекбокса задано через
                aria-labelledby, поэтому диктор читает фразу целиком. */}
            <div className="flex items-start gap-2 mb-6">
              <input
                type="checkbox"
                id="consent"
                name="consent"
                required
                aria-labelledby="consent-text"
                className="mt-1"
              />
              <p id="consent-text" className="text-sm text-gray-400">
                <label htmlFor="consent" className="cursor-pointer">
                  {data.form.consentPrefix}
                </label>{' '}
                <Link
                  to={data.form.consentUrl}
                  className="text-primary underline underline-offset-2 hover:text-primary-light"
                >
                  {data.form.consentLinkText}
                </Link>
              </p>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Отправка...' : data.form.submitText}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  )
}
